import type { ReactElement } from 'react';
import type { RouteObject } from 'react-router';
import { WORKS_OFFLINE } from '../../core/pwa/works-offline';
import { HELP_PAGES } from './generated/manifest';

/**
 * 033 FR-001, FR-004, FR-011 — the guide's routes, one per source page, GENERATED rather than
 * parameterised (topology only; `generated/manifest.ts` is the generated part this file reads).
 *
 * A `:slug` route would match `/help/anything` and then have to decide, at runtime, that the page
 * does not exist. Exact paths mean `/help/bogus` matches no child, the router falls through to the
 * application's own not-found route (`app.routes.tsx`'s wildcard), with nothing written here
 * (research R-009). FR-011 is satisfied by a route table that cannot be wrong rather than by a
 * branch somebody has to remember.
 *
 * **Every page's HTML is behind its own `lazy()`, and every one of those loaders imports the SAME
 * module — `./generated/pages`.** A bundler splits a module imported only from inside `lazy()`
 * loaders into its own chunk, and it shares that chunk across every call site that imports the same
 * specifier: ten loaders, one chunk, exactly the property Angular bought with one `loadChildren`
 * line for the whole feature (React Router's `lazy` cannot return `children`, so there is no single
 * point to hang that on here). `generated/manifest.ts` is deliberately the ONLY thing this file
 * imports at its own top level, because that module carries no HTML at all — importing
 * `./generated/pages` here, even just to read it, would pull the whole guide into whatever chunk
 * this file itself ends up in.
 *
 * The wrapper renders the page's HTML with `dangerouslySetInnerHTML`. That HTML is not user input:
 * it is rendered at BUILD TIME, by `scripts/help/render.ts`, from Markdown this repository's own
 * maintainers commit under `docs/user/` — the same trust boundary Angular's `templateUrl` sat on,
 * which compiled the same HTML into a template with no runtime sanitizer pass either. There is
 * nothing here for a sanitizer to check that the generator itself has not already decided.
 */
function pageLoader(id: string): () => Promise<{ Component: () => ReactElement }> {
  return async () => {
    const { HELP_HTML } = await import('./generated/pages');
    function HelpPageView(): ReactElement {
      return <div className="help-prose" dangerouslySetInnerHTML={{ __html: HELP_HTML[id] }} />;
    }
    return { Component: HelpPageView };
  };
}

export const routes: RouteObject[] = [
  {
    lazy: async () => ({ Component: (await import('./help-shell')).default }),
    // 033 FR-022. Every other screen in this application needs the server; the guide needs nothing
    // — it is static text already on the device. `handle` is React Router's counterpart to
    // Angular's route `data`, read (once the offline shell is ported — see `layout.tsx`'s own
    // note) with `useMatches()` rather than `ActivatedRoute.data`, since React Router has no
    // service to inject: the route match itself is the value. Carried forward now, unconsumed,
    // rather than left for that port to reinvent — the flag belongs beside the route, at the place
    // someone writing one is already looking, not in a shell that does not know about `/help` yet.
    handle: { [WORKS_OFFLINE]: true },
    children: HELP_PAGES.map((page): RouteObject =>
      page.path === ''
        ? { index: true, lazy: pageLoader(page.id) }
        : { path: page.path, lazy: pageLoader(page.id) },
    ),
  },
];
